import React, { useCallback, useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import aiAssistantService from "../../services/aiAssistantService";
import { canSendTurn, errorDetails, isAssistantRequestThrottle, newTurnId } from "./aiAssistantState";
import { assistantLifecycleReducer, createAssistantLifecycle, createTurnEnvelope, selectAssistantPanelState, selectHistoryOperation, selectOpeningOperation, selectTurnOperation } from "./aiAssistantLifecycle";
import robotNatural from "./assets/BBOT-natural.svg";
import robotBlushing from "./assets/BBOT Blushing.svg";
import robotSelections from "./assets/BBOT-selections.svg";
import pathBackdrop from "./assets/Path 443.svg";
import sendIcon from "./assets/Send.svg";
import promptMarkIcon from "./assets/Prompt-mark.svg";
import backIcon from "./assets/Back.svg";
import moreIcon from "./assets/More.svg";
import questionIcon from "./assets/Question.svg";
import documentIcon from "./assets/Document.svg";
import chartIcon from "./assets/Chart.svg";
import menuTrashIcon from "./assets/Menu-trash.svg";
import { AnswerReviewButtons, AnswerReviewDialog, REVIEW_SHEET_EXIT_MS } from "./AiAssistantAnswerReview";
import "./AiAssistantPanel.css";

const ANSWER_KIND_TEXT = {
    OFFICIAL: "התשובה נוצרה על ידי AI ועלולה לכלול טעויות — מומלץ לבדוק מול נוסח החוק",
    BTAX_MATERIALS: "מידע עזר של B-TAX — התשובה נוצרה על ידי AI ועלולה לכלול טעויות"
};
const LEGACY_ASSISTANT_LINES = new Set([
  "התשובה מבוססת על מקורות ממשלתיים רשמיים",
  "התשובה מבוססת על מקורות רשמיים של Btax",
  "הסבר כללי, אינו מהווה חוות דעת משפטית",
  "מידע עזר של B-TAX — מומלץ לאמת מול הנוסח הרשמי של החוק"
]);

const ASSISTANT_HINTS = [
  { text: "איך אתה יכול לעזור?", icon: questionIcon, response: ["איזה כיף ששאלת!\n\nאני כאן כדי לעזור לך בכל מה שקשור לעסקה שלך: אפשר לשאול אותי שאלות מקצועיות, לקבל הסבר על שדות ושלבים בטופס העסקה, ולמלא בטופס פרטים מתוך ההודעות שלך.", "ואם אחשוב שבמקרה שלך עדיף שאיש מקצוע מטעמנו יבדוק את הדברים לעומק, אדע גם להפנות אותך לגורם המתאים."] },
  { text: "אשמח ליצור קשר", icon: documentIcon, response: ["בשמחה!\n\nאפשר להתקשר אלינו, ואחד מהנציגים שלנו ישמח לעזור.", "תמיכה טכנית: 03-5615788\nתמיכה מקצועית: 050-5927888"] },
  { text: "איך ממלאים מכירה של חלק מהזכויות?", icon: chartIcon }
];
const DRAFT_CHANGE_NOTICE = "עוזר ה-AI עדכן את טופס העסקה. יש לבדוק את השינויים — העוזר עלול לטעות.";
const STALE_FORM_NOTICE = "הטופס השתנה בזמן שהעוזר ענה, ולכן השינויים מהתשובה הזו לא הוחלו בטופס. אפשר לבקש שוב.";
const GREETING_NAME_PLACEHOLDERS = new Set(["undefined", "{fullName}"]);
const INTRO_HOLD_MS = 1600;
const INTRO_HINTS_DELAY_MS = 600;
const BLUSH_MS = 5000;
const ANALYSIS_READY_NOTICE = "יש בטופס מספיק נתונים לניתוח. אפשר ללחוץ על «נתח עסקה».";

export function answerKindText(answerKind) {
  return ANSWER_KIND_TEXT[answerKind] || null;
}

export function sanitizeAssistantText(text) {
  const isSignatureLine = (line) => {
    const trimmedLine = line.trim();
    const unwrappedLine = trimmedLine.replace(/\.$/, "").replace(/^\*\*(.*)\*\*$|^\*(.*)\*$|^_(.*)_$/, "$1$2$3").replace(/\.$/, "").trim();
    return [...Object.values(ANSWER_KIND_TEXT), ...LEGACY_ASSISTANT_LINES].some((marker) => unwrappedLine === marker);
  };
  return (text || "").split("\n").filter((line) => !isSignatureLine(line)).join("\n").trim();
}

function message(role, text, answer, metadata = {}) {
  return { role, text: role === "assistant" && !metadata.assistantMessage ? sanitizeAssistantText(text) : text, answerKind: answer?.kind || null, ...metadata };
}

// A history row cannot know what this page's form holds, so it never says the assistant changed the form.
// Only an answered assistant row can be rated; reviewTurnId names it for the feedback route.
function historyMessage(turn) {
  const text = turn.text || (turn.role === "assistant" ? "לא התקבלה תשובה לשאלה זו." : "");
  const reviewable = turn.role === "assistant" && turn.text && turn.turnId;
  return message(turn.role, text, turn.role === "assistant" ? { kind: turn.answerKind } : null, { historySeq: turn.seq, ...(turn.assistantMessage ? { assistantMessage: turn.assistantMessage } : {}), ...(reviewable ? { reviewTurnId: turn.turnId, review: turn.review || null, confirmedReview: turn.review || null } : {}) });
}

function renderMixedDirectionText(text) {
  const parts = String(text || "").split(/(\b\d{2,3}-\d{7}\b|₪[\d,]+|\b\d{1,2}\/\d{1,2}\/\d{4}\b|correlationRef=[A-Za-z0-9_-]+|\b[0-9a-f]{8,}\b)/gi);
  return parts.map((part, index) => /^(?:\d{2,3}-\d{7}|₪[\d,]+|\d{1,2}\/\d{1,2}\/\d{4}|correlationRef=[A-Za-z0-9_-]+|[0-9a-f]{8,})$/i.test(part) ? <span key={index} dir="ltr" className="ai-assistant-ltr">{part}</span> : part);
}

export function renderAssistantText(text) {
  const source = String(text || "");
  const parts = [];
  const boldPattern = /\*\*([\s\S]+?)\*\*/g;
  let lastIndex = 0;
  let match;
  while ((match = boldPattern.exec(source))) {
    if (match.index > lastIndex) parts.push(<React.Fragment key={`text-${lastIndex}`}>{renderMixedDirectionText(source.slice(lastIndex, match.index))}</React.Fragment>);
    parts.push(<strong key={`bold-${match.index}`}>{renderMixedDirectionText(match[1])}</strong>);
    lastIndex = boldPattern.lastIndex;
  }
  if (!parts.length) return renderMixedDirectionText(source);
  if (lastIndex < source.length) parts.push(<React.Fragment key={`text-${lastIndex}`}>{renderMixedDirectionText(source.slice(lastIndex))}</React.Fragment>);
  return parts;
}

// Parts arrive in host order. Old history is one opaque text block; never infer receipts from prose.
export function renderAssistantMessage(item) {
  if (item.assistantMessage?.schemaVersion !== "AssistantMessageV1" || !Array.isArray(item.assistantMessage.parts)) return renderAssistantText(item.text);
  return item.assistantMessage.parts.map((part, index) => {
    if (part.kind === "FACT_RECEIPT" && item.staleFormChange) return null;
    if (part.kind === "OFFICIAL_SOURCE") {
      const safeUrl = typeof part.url === "string" && /^https:\/\//i.test(part.url);
      return <div key={index} className="ai-assistant-source" data-message-part={part.kind}>{part.title}{safeUrl ? <> — <a href={part.url} target="_blank" rel="noopener noreferrer">קישור רשמי</a></> : null}</div>;
    }
    const className = { BODY: "ai-assistant-answer-body", FACT_RECEIPT: "ai-assistant-fact-receipt", VERIFICATION_NOTE: "ai-assistant-verification-note", HOST_NOTICE: "ai-assistant-host-notice" }[part.kind];
    return className ? <div key={index} className={className} data-message-part={part.kind}>{renderAssistantText(part.text)}</div> : null;
  });
}

export default function AiAssistantPanel({ open, onOpenChange, dealId, calc, manage, getDealState, getFormProgress, formProgress, onFacts, onFormChange, fullName, answerReviewEnabled = false }) {
  const [lifecycle, dispatch] = useReducer(assistantLifecycleReducer, dealId, createAssistantLifecycle);
  const state = selectAssistantPanelState(lifecycle);
  const openingOperation = selectOpeningOperation(lifecycle);
  const historyOperation = selectHistoryOperation(lifecycle);
  const turnOperation = selectTurnOperation(lifecycle);
  const composerRestore = lifecycle.turn.restoreComposer;
  const [input, setInput] = useState("");
  const [introHasPlayed, setIntroHasPlayed] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [introDocked, setIntroDocked] = useState(false);
  const [introTraveling, setIntroTraveling] = useState(false);
  const [introFading, setIntroFading] = useState(false);
  const [introHintsVisible, setIntroHintsVisible] = useState(false);
  const [isBlushing, setIsBlushing] = useState(false);
  const [launcherReturning, setLauncherReturning] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reviewDialogTurnId, setReviewDialogTurnId] = useState(null);
  const [reviewDialogLeaving, setReviewDialogLeaving] = useState(false);
  const [reviewDialogSaving, setReviewDialogSaving] = useState(false);
  const [reviewDialogFailed, setReviewDialogFailed] = useState(false);
  const [reviewDialogOpenCount, setReviewDialogOpenCount] = useState(0);
  const [reviewToastVisible, setReviewToastVisible] = useState(false);
  const launcherRef = useRef(null);
  const launcherReturnTimeoutRef = useRef(null);
  const introDockTimeoutRef = useRef(null);
  const introHintsTimeoutRef = useRef(null);
  const blushTimeoutRef = useRef(null);
  const menuButtonRef = useRef(null);
  const inputRef = useRef(null);
  const inputMeasureRef = useRef(null);
  const composerRef = useRef(null);
  const avatarRef = useRef(null);
  const introMessageRef = useRef(null);
  const introFromRectRef = useRef(null);
  const bodyRef = useRef(null);
  const lowerStageRef = useRef(null);
  const threadRef = useRef(null);
  const nodeRef = useRef(null);
  const replyNodeRef = useRef(null);
  const pendingReplyMessageRef = useRef(null);
  const openRef = useRef(open);
  const requestGenerationRef = useRef(0);
  const messagesRef = useRef(state.messages);
  const reviewSavesRef = useRef(new Map());
  const closePanelRef = useRef(null);
  const closeReviewDialogRef = useRef(null);
  const reviewDialogTimeoutRef = useRef(null);
  const reviewDialogTokenRef = useRef(0);
  const reviewToastTimeoutRef = useRef(null);
  const executedOperationsRef = useRef(new Set());
  const handledThrottleExpiryRef = useRef(null);
  const openSessionRef = useRef(null);
  const previousDealRef = useRef(dealId);
  const formSnapshotRef = useRef(null);
  const historyAnchorRef = useRef(null);
  const scrollToBottomRef = useRef(false);
  const followLatestMessageRef = useRef(true);
  const closedScrollTopRef = useRef(null);
  const closedMessageCountRef = useRef(null);
  const reopenScrollTopRef = useRef(null);
  const lastMessageText = state.messages.length ? state.messages[state.messages.length - 1].text : null;
  const dailyResetAt = state.daily.resetAt ? Date.parse(state.daily.resetAt) : null;
  const dailyLimitReached = state.daily.remaining === 0 && (!Number.isFinite(dailyResetAt) || dailyResetAt > Date.now());
  const limitReached = dailyLimitReached && Number.isFinite(dailyResetAt) && dailyResetAt > Date.now();
  const requestLimited = Boolean(state.throttle && state.throttle.until > Date.now());
  const introIsDocked = introDocked || introHasPlayed;
  const suggestionsVisible = showIntro && (introHintsVisible || introHasPlayed) && !state.isOpening && !state.error && !state.unavailable && !state.credentialSuperseded && state.messages.length === 0 && !input.trim();
  const robotPose = suggestionsVisible ? "selections" : isBlushing && !state.isSending ? "blushing" : "natural";
  messagesRef.current = state.messages;
  openRef.current = open;

  useEffect(() => {
    if (!Number.isFinite(dailyResetAt)) return undefined;
    const epoch = lifecycle.context.epoch;
    const resetAt = state.daily.resetAt;
    const reset = () => dispatch({ type: "DAILY_RESET", epoch, resetAt, now: Date.now() });
    if (dailyResetAt <= Date.now()) {
      reset();
      return undefined;
    }
    const timeout = window.setTimeout(reset, dailyResetAt - Date.now());
    return () => window.clearTimeout(timeout);
  }, [dailyResetAt, state.daily.resetAt, lifecycle.context.epoch]);

  useEffect(() => {
    if (!state.throttle || state.throttle.expired) return undefined;
    const { until } = state.throttle;
    const epoch = lifecycle.context.epoch;
    let timeout;
    const expire = () => {
      const now = Date.now();
      const remaining = until - now;
      if (remaining > 0) timeout = window.setTimeout(expire, remaining);
      else dispatch({ type: "THROTTLE_EXPIRED", epoch, until, now });
    };
    expire();
    return () => window.clearTimeout(timeout);
  }, [state.throttle, lifecycle.context.epoch]);

  useEffect(() => {
    if (!state.throttle?.expired || handledThrottleExpiryRef.current === state.throttle) return;
    handledThrottleExpiryRef.current = state.throttle;
    if (!open || reviewDialogTurnId || inputRef.current?.disabled) return;
    const focused = document.activeElement;
    if (focused === document.body || focused === nodeRef.current || composerRef.current?.contains(focused)) inputRef.current?.focus();
  }, [state.throttle, open, reviewDialogTurnId]);

  // Opening requests execute only after reducer admission. This registry owns effect execution, not admission.
  useEffect(() => {
    if (!openingOperation || executedOperationsRef.current.has(openingOperation)) return;
    executedOperationsRef.current.add(openingOperation);
    if (openingOperation.reason === "new") {
      requestGenerationRef.current += 1;
      setInput("");
      setMenuOpen(false);
      setShowIntro(false);
      setIntroHasPlayed(false);
      setIntroDocked(false);
      setIntroTraveling(false);
      setIntroFading(false);
      setIntroHintsVisible(false);
      clearBlush();
    }
    const { key, epoch, reason } = openingOperation;
    aiAssistantService.openSession(openingOperation.dealId, reason === "new")
      .then(({ history, ...session }) => dispatch({
        type: "OPEN_SUCCEEDED", key, epoch, session, history,
        messages: reason === "new" ? [] : (history?.turns || []).slice().reverse().map(historyMessage)
      }))
      .catch((error) => dispatch({ type: "OPEN_FAILED", key, epoch, error: errorDetails(error), now: Date.now() }))
      .finally(() => executedOperationsRef.current.delete(openingOperation));
  }, [openingOperation]);

  useLayoutEffect(() => {
    if (!lifecycle.snapshotVersion) return;
    historyAnchorRef.current = null;
    setShowIntro(messagesRef.current.length === 0);
    scrollToBottomRef.current = true;
  }, [lifecycle.snapshotVersion]);

  const openSession = (force = false) => dispatch({ type: "OPEN_REQUESTED", visible: open, reason: force ? "retry" : "initial", now: Date.now() });
  openSessionRef.current = openSession;
  useEffect(() => () => {
    window.clearTimeout(launcherReturnTimeoutRef.current);
    window.clearTimeout(introDockTimeoutRef.current);
    window.clearTimeout(introHintsTimeoutRef.current);
    window.clearTimeout(blushTimeoutRef.current);
  }, []);
  const dockIntro = (travel = true) => {
    const intro = introMessageRef.current;
    introFromRectRef.current = travel && intro ? intro.getBoundingClientRect() : null;
    setIntroDocked(true);
    setIntroTraveling(Boolean(introFromRectRef.current));
    setIntroFading(!travel);
  };

  useEffect(() => {
    if (!open || !showIntro || introHasPlayed || state.isOpening || state.messages.length) return undefined;
    introDockTimeoutRef.current = window.setTimeout(() => {
      dockIntro();
      introHintsTimeoutRef.current = window.setTimeout(() => {
        setIntroHintsVisible(true);
        setIntroHasPlayed(true);
      }, INTRO_HINTS_DELAY_MS);
    }, INTRO_HOLD_MS);
    return () => {
      window.clearTimeout(introDockTimeoutRef.current);
      window.clearTimeout(introHintsTimeoutRef.current);
    };
  }, [open, showIntro, introHasPlayed, state.isOpening, state.messages.length]);

  const clearBlush = () => {
    window.clearTimeout(blushTimeoutRef.current);
    blushTimeoutRef.current = null;
    setIsBlushing(false);
  };

  const startBlush = () => {
    if (!openRef.current) return;
    window.clearTimeout(blushTimeoutRef.current);
    setIsBlushing(true);
    blushTimeoutRef.current = window.setTimeout(() => {
      blushTimeoutRef.current = null;
      setIsBlushing(false);
    }, BLUSH_MS);
  };

  const resizeInput = () => {
    const textarea = inputRef.current;
    const measure = inputMeasureRef.current;
    if (!textarea || !measure) return;
    measure.style.width = `${textarea.offsetWidth}px`;
    const styles = window.getComputedStyle(textarea);
    const minHeight = parseFloat(styles.minHeight) || 0;
    const maxHeight = parseFloat(styles.maxHeight) || Infinity;
    const contentHeight = measure.scrollHeight;
    textarea.style.height = `${Math.max(minHeight, Math.min(contentHeight, maxHeight))}px`;
    textarea.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
  };
  useEffect(() => {
    if (!open) return;
    if (closedMessageCountRef.current !== null) {
      if (messagesRef.current.length > closedMessageCountRef.current || scrollToBottomRef.current) {
        scrollToBottomRef.current = true;
      } else {
        reopenScrollTopRef.current = closedScrollTopRef.current;
      }
      closedScrollTopRef.current = null;
      closedMessageCountRef.current = null;
    }
    openSessionRef.current();
    window.requestAnimationFrame(() => nodeRef.current?.focus());
  }, [open]);

  useEffect(() => {
    const thread = threadRef.current;
    if (!thread) return;
    const anchor = historyAnchorRef.current;
    if (anchor && anchor.key === lifecycle.historyAppliedKey) {
      historyAnchorRef.current = null;
      thread.scrollTop = anchor.top + thread.scrollHeight - anchor.height;
      followLatestMessageRef.current = thread.scrollHeight - thread.scrollTop - thread.clientHeight <= 24;
    } else if (reopenScrollTopRef.current !== null) {
      thread.scrollTop = reopenScrollTopRef.current;
      reopenScrollTopRef.current = null;
      followLatestMessageRef.current = thread.scrollHeight - thread.scrollTop - thread.clientHeight <= 24;
    } else if (scrollToBottomRef.current || state.isSending) {
      scrollToBottomRef.current = false;
      thread.scrollTop = thread.scrollHeight;
      followLatestMessageRef.current = true;
    }
  }, [open, state.isOpening, state.isSending, state.messages.length, lastMessageText, lifecycle.historyAppliedKey]);

  useEffect(() => {
    const thread = threadRef.current;
    if (!open || !thread || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      if (followLatestMessageRef.current) thread.scrollTop = thread.scrollHeight;
    });
    observer.observe(thread);
    thread.querySelectorAll(".ai-assistant-bubble, .ai-assistant-greeting").forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, [open, state.messages.length]);

  useLayoutEffect(() => {
    const thread = threadRef.current;
    const composer = composerRef.current;
    const pendingReply = pendingReplyMessageRef.current;
    const reply = replyNodeRef.current;
    if (!pendingReply || !messagesRef.current.includes(pendingReply)) return;
    if (!reply || !composer) return;
    thread.scrollTop = thread.scrollHeight;
    const threadRect = thread.getBoundingClientRect();
    const replyRect = reply.getBoundingClientRect();
    const composerRect = composer.getBoundingClientRect();
    const avatarRect = avatarRef.current?.getBoundingClientRect();
    const originTop = avatarRect ? avatarRect.top : composerRect.top;
    const requestedTravel = Math.max(0, originTop - replyRect.bottom);
    const availableTravel = Math.max(0, threadRect.height - Math.min(replyRect.height, threadRect.height));
    const distance = Math.min(requestedTravel, availableTravel);
    reply.style.setProperty("--assistant-arrival-y", `${distance}px`);
    pendingReplyMessageRef.current = null;
  }, [state.messages.length]);

  useLayoutEffect(() => {
    const from = introFromRectRef.current;
    const intro = introMessageRef.current;
    if (!introTraveling || !from || !intro) return;
    introFromRectRef.current = null;
    const to = intro.getBoundingClientRect();
    intro.style.setProperty("--assistant-intro-y", `${Math.round(from.top - to.top)}px`);
  }, [introTraveling, introDocked]);

  useLayoutEffect(() => {
    resizeInput();
  }, [input, open]);

  useLayoutEffect(() => {
    const panel = nodeRef.current;
    const layout = panel?.closest(".deal-assistant-layout");
    if (!open || !panel || !layout) return undefined;
    const syncAvailableHeight = () => {
      const naturalTop = layout.getBoundingClientRect().top + window.scrollY;
      panel.style.setProperty("--assistant-available-height", `${Math.max(0, document.documentElement.clientHeight - naturalTop)}px`);
    };
    syncAvailableHeight();
    window.addEventListener("resize", syncAvailableHeight);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(syncAvailableHeight);
    observer?.observe(document.documentElement);
    return () => {
      window.removeEventListener("resize", syncAvailableHeight);
      observer?.disconnect();
    };
  }, [open]);

  useLayoutEffect(() => {
    const body = bodyRef.current;
    const stage = lowerStageRef.current;
    if (!open || !body || !stage) return undefined;
    const syncStageHeight = () => body.style.setProperty("--assistant-lower-stage-height", `${stage.offsetHeight}px`);
    syncStageHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(syncStageHeight);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener("resize", resizeInput);
    return () => window.removeEventListener("resize", resizeInput);
  }, [open]);

  useEffect(() => {
    if (previousDealRef.current === dealId) return;
    previousDealRef.current = dealId;
    requestGenerationRef.current += 1;
    cancelHistoryRequest();
    setInput("");
    dispatch({ type: "DEAL_CHANGED", dealId, now: Date.now() });
    setShowIntro(false);
    setIntroHasPlayed(false);
    setIntroDocked(false);
    setIntroTraveling(false);
    setIntroFading(false);
    setIntroHintsVisible(false);
    clearBlush();
    if (openRef.current) {
      dispatch({ type: "OPEN_REQUESTED", visible: true, reason: "initial", now: Date.now() });
      window.requestAnimationFrame(() => nodeRef.current?.focus());
    }
  }, [dealId]);

  useEffect(() => {
    const snapshot = JSON.stringify({ calc, manage });
    if (formSnapshotRef.current !== null && formSnapshotRef.current !== snapshot && onFormChange) onFormChange();
    formSnapshotRef.current = snapshot;
  }, [calc, manage, onFormChange]);

  const startNewConversation = () => dispatch({ type: "OPEN_REQUESTED", visible: open, reason: "new", now: Date.now() });

  const loadPreviousHistory = (retry = false) => {
    if (!threadRef.current) return;
    dispatch({ type: "HISTORY_REQUESTED", retry, now: Date.now() });
  };

  useEffect(() => {
    if (!historyOperation || executedOperationsRef.current.has(historyOperation)) return;
    executedOperationsRef.current.add(historyOperation);
    const thread = threadRef.current;
    historyAnchorRef.current = thread ? { key: historyOperation.key, top: thread.scrollTop, height: thread.scrollHeight } : null;
    const { key, epoch, historyEpoch, beforeSeq } = historyOperation;
    aiAssistantService.getHistory(historyOperation.dealId, beforeSeq)
      .then((history) => dispatch({ type: "HISTORY_SUCCEEDED", key, epoch, historyEpoch, history, messages: (history.turns || []).slice().reverse().map(historyMessage) }))
      .catch((error) => dispatch({ type: "HISTORY_FAILED", key, epoch, historyEpoch, error: errorDetails(error), now: Date.now() }))
      .finally(() => executedOperationsRef.current.delete(historyOperation));
  }, [historyOperation]);

  const cancelHistoryRequest = () => {
    dispatch({ type: "HISTORY_CANCELLED" });
    historyAnchorRef.current = null;
  };

  // Only an admitted applying operation may project facts. Completion commits position, reply and readiness together.
  const completeTurn = useCallback(async (operation, requestGeneration) => {
    if (requestGeneration !== requestGenerationRef.current) return;
    const { result, envelope, key, epoch } = operation;
    if (result.correlationRef) console.info("AI assistant request completed", result.correlationRef);
    const facts = Array.isArray(result.facts) ? result.facts : [];
    const projection = facts.length && onFacts ? await onFacts(facts, envelope.turnContext) : null;
    if (requestGeneration !== requestGenerationRef.current) return;
    const changedDraft = Boolean(projection?.events?.length);
    const progress = getFormProgress?.();
    const reply = message("assistant", result.assistantText, result.answer, {
      ...(result.assistantMessage ? { assistantMessage: result.assistantMessage } : {}),
      ...(openRef.current ? { presentation: "reply" } : {}),
      reviewTurnId: envelope.request.turnId, review: null, confirmedReview: null,
      ...(projection?.formChangedDuringTurn ? { staleFormChange: true } : changedDraft ? { changedDraft: true } : {})
    });
    pendingReplyMessageRef.current = reply.presentation ? reply : null;
    scrollToBottomRef.current = true;
    dispatch({ type: "TURN_COMPLETED", key, epoch, reply, changedDraft, progress, readyNotice: message("assistant", ANALYSIS_READY_NOTICE) });
    startBlush();
  }, [onFacts, getFormProgress]);

  // Execute committed turn phases before another interaction can observe an unfinished immediate response.
  useLayoutEffect(() => {
    if (!turnOperation || turnOperation.dealId !== dealId || executedOperationsRef.current.has(turnOperation)) return;
    executedOperationsRef.current.add(turnOperation);
    const { key, epoch, kind, envelope } = turnOperation;
    const requestGeneration = requestGenerationRef.current;
    const failed = (error) => {
      const details = errorDetails(error);
      if (details.correlationRef) console.warn("AI assistant request failed", details.correlationRef);
      dispatch({ type: "TURN_FAILED", key, epoch, error, now: Date.now() });
    };
    let execution;
    if (kind === "sending") {
      if (turnOperation.clearComposer) {
        setInput("");
        if (showIntro && !introIsDocked) dockIntro(false);
        if (showIntro) setIntroHasPlayed(true);
      }
      scrollToBottomRef.current = true;
      execution = aiAssistantService.sendTurn({ ...envelope.request, sessionIdRef: turnOperation.session.sessionIdRef })
        .then((result) => dispatch({ type: "TURN_RESPONSE_RECEIVED", key, epoch, result }));
    } else if (kind === "renewing") {
      execution = aiAssistantService.openSession(turnOperation.dealId, false)
        .then(({ history, ...session }) => dispatch({ type: "TURN_RENEWED", key, epoch, session }));
    } else if (kind === "resyncing") {
      execution = aiAssistantService.openSession(turnOperation.dealId, false)
        .then(({ history, ...session }) => dispatch({ type: "TURN_RESYNCED", key, epoch, session, history, messages: (history?.turns || []).slice().reverse().map(historyMessage) }));
    } else if (kind === "applying") {
      execution = completeTurn(turnOperation, requestGeneration);
    }
    execution.catch(failed).finally(() => executedOperationsRef.current.delete(turnOperation));
  }, [turnOperation, dealId, completeTurn, showIntro, introIsDocked]);

  useLayoutEffect(() => {
    const executedOperations = executedOperationsRef.current;
    if (!composerRestore || composerRestore.epoch !== lifecycle.context.epoch || executedOperations.has(composerRestore)) return;
    executedOperations.add(composerRestore);
    setInput((current) => current.trim() ? current : composerRestore.text);
    return () => executedOperations.delete(composerRestore);
  }, [composerRestore, lifecycle.context.epoch]);

  const updateMessageReview = (reviewTurnId, review) => dispatch({ type: "MESSAGE_REVIEW_UPDATED", epoch: lifecycle.context.epoch, reviewTurnId, review });

  // The rating shows at once. Saves of one answer run in order, so a thumbs-down cannot overwrite its later comment;
  // Each success records the server-confirmed review, even if a later save is queued. Only the latest save changes
  // the displayed rating; its failure rolls back to that confirmed review rather than another optimistic value.
  const saveAnswerReview = (reviewTurnId, review, reportThumbFailure = true) => {
    if (requestLimited) return Promise.resolve(false);
    const epoch = lifecycle.context.epoch;
    dispatch({ type: "THROTTLE_CLEARED", epoch, now: Date.now() });
    updateMessageReview(reviewTurnId, { review, reviewSaving: true, reviewFailed: false });
    const prior = reviewSavesRef.current.get(reviewTurnId) || Promise.resolve();
    const isLatest = () => reviewSavesRef.current.get(reviewTurnId) === saving;
    const saving = prior.then(() => aiAssistantService.saveFeedback({ schemaVersion: "AiAssistantFeedbackV1", dealId, turnId: reviewTurnId,
      rating: review ? review.rating : null, reasons: review ? review.reasons : [], comment: review ? review.comment : null }))
      .then((result) => {
        const confirmedReview = result.review || null;
        updateMessageReview(reviewTurnId, { confirmedReview, ...(isLatest() ? { review: confirmedReview, reviewSaving: false } : {}) });
        return true;
      },
        (error) => {
          if (isAssistantRequestThrottle(error)) dispatch({ type: "REQUEST_THROTTLED", epoch, error, now: Date.now() });
          if (isLatest()) dispatch({ type: "MESSAGE_REVIEW_SAVE_FAILED", epoch, reviewTurnId, reportThumbFailure });
          return false;
        })
      .finally(() => { if (isLatest()) reviewSavesRef.current.delete(reviewTurnId); });
    reviewSavesRef.current.set(reviewTurnId, saving);
    return saving;
  };

  // A second click on the chosen thumb removes the rating. Details are asked only from the "רוצה לפרט?" link, never by a pop-up.
  const rateAnswer = (item, rating) => {
    if (item.review?.rating === rating) return saveAnswerReview(item.reviewTurnId, null);
    saveAnswerReview(item.reviewTurnId, { rating, reasons: [], comment: null });
  };

  const openReviewDialog = (reviewTurnId) => {
    const item = messagesRef.current.find((message) => message.reviewTurnId === reviewTurnId);
    if (!item?.review?.rating || item.reviewSaving || reviewSavesRef.current.has(reviewTurnId)) return;
    reviewDialogTokenRef.current += 1;
    window.clearTimeout(reviewDialogTimeoutRef.current);
    setReviewDialogLeaving(false);
    setReviewDialogSaving(false);
    setReviewDialogFailed(false);
    setReviewDialogOpenCount((count) => count + 1);
    setReviewDialogTurnId(reviewTurnId);
  };

  // The sheet slides down before it leaves the DOM; focus goes back to the link that opened it.
  const closeReviewDialog = () => {
    if (!reviewDialogTurnId || reviewDialogLeaving) return;
    reviewDialogTokenRef.current += 1;
    const reviewTurnId = reviewDialogTurnId;
    setReviewDialogLeaving(true);
    window.clearTimeout(reviewDialogTimeoutRef.current);
    const focusOpener = () => {
      if (!composerRef.current?.contains(document.activeElement)) threadRef.current?.querySelector(`[data-review-turn="${reviewTurnId}"]`)?.focus({ preventScroll: true });
    };
    reviewDialogTimeoutRef.current = window.setTimeout(() => {
      setReviewDialogTurnId(null);
      setReviewDialogLeaving(false);
      // Focus may still sit in the removed sheet when the browser delayed the frame callback below.
      if (!document.activeElement || document.activeElement === document.body || document.activeElement.closest(".ai-assistant-review-layer")) focusOpener();
    }, REVIEW_SHEET_EXIT_MS);
    window.requestAnimationFrame(focusOpener);
  };
  closeReviewDialogRef.current = closeReviewDialog;

  const showReviewToast = () => {
    setReviewToastVisible(true);
    window.clearTimeout(reviewToastTimeoutRef.current);
    reviewToastTimeoutRef.current = window.setTimeout(() => setReviewToastVisible(false), 2200);
  };

  const saveReviewDetails = (review) => {
    const token = reviewDialogTokenRef.current;
    const generation = requestGenerationRef.current;
    setReviewDialogSaving(true);
    setReviewDialogFailed(false);
    saveAnswerReview(reviewDialogTurnId, review, false).then((saved) => {
      // An older save still updates its answer, but cannot close a newer sheet or discard its draft.
      if (token !== reviewDialogTokenRef.current || generation !== requestGenerationRef.current) return;
      setReviewDialogSaving(false);
      setReviewDialogFailed(!saved);
      if (saved) { closeReviewDialog(); showReviewToast(); }
    });
  };

  useEffect(() => () => {
    window.clearTimeout(reviewDialogTimeoutRef.current);
    window.clearTimeout(reviewToastTimeoutRef.current);
  }, []);

  const reviewDialogMessage = reviewDialogTurnId ? state.messages.find((item) => item.reviewTurnId === reviewDialogTurnId) || null : null;
  const latestReviewTurnId = answerReviewEnabled ? [...state.messages].reverse().find((item) => item.role === "assistant" && item.reviewTurnId)?.reviewTurnId : null;

  // A new question captures its immutable request once. Retry and renewal reuse it, changing only the transport credential.
  const submitTurn = (text, fromHint = false, composerText = text) => {
    if (!canSendTurn(state, text) || limitReached || requestLimited) return;
    if (lifecycle.turn.tag === "retryable" && isAssistantRequestThrottle(lifecycle.turn.error) && text === lifecycle.turn.envelope.request.input) {
      retryLastTurn();
      return;
    }
    const turnId = newTurnId();
    if (!turnId) {
      dispatch({ type: "TURN_ID_FAILED", epoch: lifecycle.context.epoch, now: Date.now() });
      return;
    }
    const envelope = createTurnEnvelope({ schemaVersion: "AiAssistantTurnV1", dealId, turnId, input: text, expectedRevisionId: state.session.state?.revisionId || null, expectedConversationSequence: state.session.state?.sequence || 0, dealState: getDealState(), ...(formProgress ? { formProgress } : {}) }, composerText);
    dispatch({ type: "TURN_SUBMITTED", epoch: lifecycle.context.epoch, now: Date.now(), envelope,
      message: message("user", text, null, { presentation: "prompt", turnId, ...(fromHint ? { fromHint: true } : {}) }) });
  };

  const send = (event) => {
    event.preventDefault();
    submitTurn(input.trim(), false, input);
  };

  const handleInputKeyDown = (event) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    if (canSendTurn(state, input)) submitTurn(input.trim(), false, input);
  };

  const retryLastTurn = () => {
    if (requestLimited) return;
    if (lifecycle.turn.tag === "retryable" && isAssistantRequestThrottle(lifecycle.turn.error) && input.trim() === lifecycle.turn.envelope.request.input) setInput("");
    dispatch({ type: "TURN_RETRY_REQUESTED", epoch: lifecycle.context.epoch, now: Date.now() });
  };

  const sendHint = (hint) => {
    if (input.trim() || !state.session) return;
    submitTurn(hint, true);
  };

  const selectHint = (hint) => {
    if (!hint.response) {
      sendHint(hint.text);
      return;
    }
    if (input.trim() || !state.session || state.isSending || state.isOpening) return;
    startBlush();
    scrollToBottomRef.current = true;
    dispatch({ type: "LOCAL_HINT_SELECTED", epoch: lifecycle.context.epoch,
      messages: [message("user", hint.text, null, { fromHint: true, presentation: "prompt" }), ...hint.response.map((text) => message("assistant", text, null, { localOnly: true, presentation: "reply" }))] });
  };

  const closePanel = () => {
    closedScrollTopRef.current = threadRef.current ? threadRef.current.scrollTop : null;
    closedMessageCountRef.current = messagesRef.current.length;
    cancelHistoryRequest();
    clearBlush();
    onOpenChange(false);
    setMenuOpen(false);
    setIntroHasPlayed(true);
    pendingReplyMessageRef.current = null;
    setLauncherReturning(true);
    window.clearTimeout(launcherReturnTimeoutRef.current);
    launcherReturnTimeoutRef.current = window.setTimeout(() => setLauncherReturning(false), 375);
    dispatch({ type: "MESSAGE_PRESENTATIONS_CLEARED", epoch: lifecycle.context.epoch });
    window.requestAnimationFrame(() => launcherRef.current?.focus());
  };
  closePanelRef.current = closePanel;

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      if (reviewDialogTurnId) closeReviewDialogRef.current();
      else if (menuOpen) {
        setMenuOpen(false);
        window.requestAnimationFrame(() => menuButtonRef.current?.focus());
      } else closePanelRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, menuOpen, reviewDialogTurnId]);

  const throttleAction = lifecycle.turn.tag === "retryable" ? retryLastTurn
    : lifecycle.session.tag === "throttled" ? () => openSession(true)
      : lifecycle.history.tag === "failed" ? () => loadPreviousHistory(true) : null;
  const throttleNotice = state.throttle ? { kind: "notice",
    text: (state.throttle.code === "AI_REQUEST_IN_PROGRESS"
      ? "בקשה קודמת שלך עדיין בטיפול. יש להמתין מעט ולנסות שוב."
      : "נשלחו יותר מדי בקשות בזמן קצר. יש להמתין מעט ולנסות שוב.") + (state.throttle.expired ? " אפשר לנסות שוב כעת." : ""),
    ...(throttleAction ? { action: "שלחו שוב", onAction: throttleAction } : {}) } : null;
  // Session supersession wins, then an active pause explains disabled actions. After expiry,
  // current availability/history/turn errors and the daily limit precede the old retry reminder.
  const notice = state.credentialSuperseded
    ? { kind: "error", text: "נכנסת למערכת בחלון אחר. כדי להמשיך כאן, יש להתחבר מחדש.", action: "התחברות מחדש", onAction: () => { window.location.href = "/sessions/login"; } }
    : requestLimited ? throttleNotice
    : state.unavailable
      ? { kind: "error", text: "העוזר אינו זמין לעסקה הזו.", action: "נסו שוב", onAction: () => openSession(true) }
      : lifecycle.history.tag === "failed"
        ? { kind: "error", text: "לא הצלחתי לטעון הודעות קודמות.", action: "טען שוב", onAction: () => loadPreviousHistory(true) }
      : state.error && !(throttleNotice && isAssistantRequestThrottle(state.error))
        ? state.error.code === "INPUT_TOO_LONG"
          ? { kind: "error", text: "ההודעה ארוכה מדי. יש לקצר אותה ל־4,000 תווים לכל היותר ולשלוח שוב." }
          : state.error.code === "TURN_ID_UNAVAILABLE"
            ? { kind: "error", text: "לא ניתן ליצור מזהה מאובטח לשליחה בדפדפן הזה." }
            : { kind: "error", text: state.error.message || "לא הצלחתי לענות. נסו לשלוח שוב.", ...(state.error.retryable === false ? {} : { action: "שלחו שוב", onAction: retryLastTurn }) }
        : dailyLimitReached
          ? { kind: "notice", text: "הגעתם למכסת ההודעות היומית. אפשר להמשיך מחר." }
          : throttleNotice;
  const safeFullName = typeof fullName === "string" ? fullName.trim() : "";
  const greetingName = safeFullName && !GREETING_NAME_PLACEHOLDERS.has(safeFullName) ? safeFullName : null;
  const introContent = showIntro && !state.isOpening && !state.error && !state.unavailable && !state.credentialSuperseded
    ? <section className="ai-assistant-greeting" aria-label="היכרות עם ביבוט"><div ref={introMessageRef} className={`ai-assistant-intro-message${introIsDocked ? introTraveling ? " ai-assistant-intro-travel" : introFading ? " ai-assistant-intro-fade" : " ai-assistant-intro-static" : " ai-assistant-speech"}`} onAnimationEnd={(event) => { if (event.animationName === "ai-assistant-intro-travel") setIntroTraveling(false); if (event.animationName === "ai-assistant-intro-fade") setIntroFading(false); }}><strong>{greetingName ? `שלום ${greetingName}!` : "שלום!"}</strong><div className="ai-assistant-intro-guidance">אני ביבוט, העוזר האישי החדש שלך!</div><div className="ai-assistant-intro-guidance">אני כאן כדי לעזור לך בעבודה על העסקה — בצורה פשוטה ונוחה!</div></div></section>
    : null;

  const robotImages = (pose) => <><img className={`ai-assistant-avatar-pose ai-assistant-avatar-pose-natural${pose === "natural" ? " ai-assistant-avatar-pose-active" : ""}`} src={robotNatural} alt="" /><img className={`ai-assistant-avatar-pose ai-assistant-avatar-pose-selections${pose === "selections" ? " ai-assistant-avatar-pose-active" : ""}`} src={robotSelections} alt="" /><img className={`ai-assistant-avatar-pose ai-assistant-avatar-pose-blushing${pose === "blushing" ? " ai-assistant-avatar-pose-active" : ""}`} src={robotBlushing} alt="" /></>;
  if (!open) return <button ref={launcherRef} className={`ai-assistant-launcher${launcherReturning ? " ai-assistant-launcher-returning" : ""}`} type="button" aria-label="עוזר AI" onClick={() => onOpenChange(true)}>{robotImages("natural")}</button>;
  return <section ref={nodeRef} tabIndex={-1} className="ai-assistant-panel" aria-label="עוזר AI" dir="rtl">
      <header className="ai-assistant-header"><button className="ai-assistant-header-control ai-assistant-close" type="button" aria-label="סגירה" onClick={closePanel}><img src={backIcon} alt="" /></button><button ref={menuButtonRef} className="ai-assistant-header-control ai-assistant-menu-button" type="button" aria-label="אפשרויות שיחה" aria-expanded={menuOpen} aria-controls="ai-assistant-menu" onClick={() => setMenuOpen((isOpen) => !isOpen)}><img src={moreIcon} alt="" /></button><div id="ai-assistant-menu" className="ai-assistant-menu" hidden={!menuOpen}><button className="ai-assistant-menu-clear" type="button" aria-label="נקה צ'אט" disabled={state.isSending || state.isOpening || requestLimited} title="פותח שיחה חדשה וריקה ומשאיר את היסטוריית השיחה הקודמת" aria-describedby="ai-assistant-clear-chat-description" onClick={startNewConversation}><img src={menuTrashIcon} alt="" /><span>נקה צ'אט</span></button><span id="ai-assistant-clear-chat-description" className="ai-assistant-sr-only">פותח שיחה חדשה וריקה ומשאיר את היסטוריית השיחה הקודמת.</span></div></header>
      <div ref={bodyRef} className="ai-assistant-body">
      <div ref={threadRef} className="ai-assistant-thread" onScroll={(event) => { const thread = event.currentTarget; followLatestMessageRef.current = thread.scrollHeight - thread.scrollTop - thread.clientHeight <= 24; if (thread.scrollTop <= 24) loadPreviousHistory(); }}>{state.isLoadingHistory ? <div className="ai-assistant-history-loading" role="status">טוענים הודעות קודמות…</div> : null}{state.isOpening ? <div className="ai-assistant-notice">פותחים שיחה…</div> : null}{state.messages.length || introIsDocked ? introContent : null}{state.messages.map((item, index) => item.type === "event" ? <div key={`event-${index}`} className="ai-assistant-event" role="status">{renderMixedDirectionText(item.text)}</div> : <React.Fragment key={item.historySeq || `${item.role}-${index}`}><div ref={item === pendingReplyMessageRef.current ? replyNodeRef : null} style={{ "--assistant-arrival-y": "0px" }} onAnimationEnd={(event) => {
        if (event.target !== event.currentTarget || !item.presentation) return;
        dispatch({ type: "MESSAGE_PRESENTATION_FINISHED", epoch: lifecycle.context.epoch, message: item });
      }} className={`ai-assistant-bubble ${item.role === "user" ? "ai-assistant-user" : "ai-assistant-bot"}${item.fromHint ? " ai-assistant-hint-message" : ""}${item.presentation ? ` ai-assistant-arrive-${item.presentation}` : ""}`}><span className="ai-assistant-who">{item.role === "user" ? "אתם" : "עוזר AI"}</span>{item.fromHint ? <img className="ai-assistant-prompt-mark" src={promptMarkIcon} alt="" /> : null}{item.role === "assistant" ? renderAssistantMessage(item) : renderMixedDirectionText(item.text)}{item.role === "assistant" && answerKindText(item.answerKind) ? <em className="ai-assistant-answer-kind">{answerKindText(item.answerKind)}</em> : null}{item.role === "assistant" && item.staleFormChange ? <em className="ai-assistant-stale-form" style={{ display: "block", marginTop: 8, color: "#5e6b84", fontSize: 13, fontStyle: "italic" }}>{STALE_FORM_NOTICE}</em> : null}{item.role === "assistant" && item.changedDraft ? <em className="ai-assistant-draft-change">{DRAFT_CHANGE_NOTICE}</em> : null}</div>{answerReviewEnabled && item.role === "assistant" && item.reviewTurnId ? <AnswerReviewButtons turnId={item.reviewTurnId} review={item.review} saving={item.reviewSaving} failed={item.reviewFailed} disabled={requestLimited} latest={item.reviewTurnId === latestReviewTurnId} arriving={item.presentation === "reply"} onRate={(rating) => rateAnswer(item, rating)} onEditComment={() => openReviewDialog(item.reviewTurnId)} /> : null}</React.Fragment>)}</div>
      <div ref={lowerStageRef} className="ai-assistant-lower-stage">
        <div className="ai-assistant-speech-slot">{!state.messages.length && !introIsDocked ? introContent : null}{suggestionsVisible ? <div className="ai-assistant-speech ai-assistant-suggestions-guidance">אפשר לכתוב טקסט חופשי, או לבחור באחת מהאפשרויות הבאות</div> : null}{state.isSending ? <div className="ai-assistant-typing" role="status" aria-label="העוזר כותב תשובה"><span className="ai-assistant-sr-only">העוזר כותב תשובה</span><span className="ai-assistant-typing-dots" aria-hidden="true"><span /><span /><span /></span></div> : null}</div>
        <div className="ai-assistant-character-stage"><div className="ai-assistant-avatar-wrap"><img className="ai-assistant-avatar-backdrop" src={pathBackdrop} alt="" /><div ref={avatarRef} className="ai-assistant-avatar">{robotImages(robotPose)}</div></div>{suggestionsVisible ? <div className="ai-assistant-hints ai-assistant-hints-docked">{ASSISTANT_HINTS.map((hint, index) => <button key={hint.text} type="button" style={{ "--assistant-hint-index": index }} onClick={() => selectHint(hint)}><img src={hint.icon} alt="" /><span>{hint.text}</span></button>)}</div> : null}</div>
      </div>
      </div>
      {notice ? <div className={`ai-assistant-bottom-notice ai-assistant-bottom-notice-${notice.kind}`} role={notice.kind === "error" ? "alert" : "status"}><span>{notice.text}</span>{notice.action ? <button type="button" onClick={notice.onAction} disabled={state.isSending || state.isOpening || requestLimited}>{notice.action}</button> : null}</div> : null}
      {answerReviewEnabled && reviewDialogMessage ? <AnswerReviewDialog key={`${reviewDialogTurnId}-${reviewDialogOpenCount}`} review={reviewDialogMessage.review} leaving={reviewDialogLeaving} saving={reviewDialogSaving} failed={reviewDialogFailed} coolingDown={requestLimited} onCancel={closeReviewDialog} onSave={saveReviewDetails} /> : null}
      {answerReviewEnabled ? <div className={`ai-assistant-review-toast${reviewToastVisible ? " ai-assistant-review-toast-show" : ""}`} role="status">{reviewToastVisible ? "הפירוט נשלח, תודה" : null}</div> : null}
      <form ref={composerRef} className="ai-assistant-composer" onSubmit={send}>{typeof state.daily.remaining === "number" && state.daily.remaining >= 1 && state.daily.remaining <= 3 ? <div className="ai-assistant-daily-remaining" role="status">הודעות שנותרו להיום: {state.daily.remaining}</div> : null}<div className="ai-assistant-entry"><textarea ref={inputRef} id="ai-assistant-message" name="aiAssistantMessage" autoComplete="off" aria-label="הודעה" placeholder="הקלד הודעה..." value={input} disabled={state.isSending || state.isOpening || limitReached || requestLimited} onChange={(event) => setInput(event.target.value)} onKeyDown={handleInputKeyDown} /><textarea ref={inputMeasureRef} id="ai-assistant-message-measure" name="aiAssistantMessageMeasure" autoComplete="off" className="ai-assistant-entry-measure" aria-hidden="true" tabIndex={-1} rows={1} value={input} readOnly /><button type="submit" aria-label="שליחה" disabled={!canSendTurn(state, input) || limitReached || requestLimited}><img src={sendIcon} alt="" /></button></div></form>
    </section>;
}
